/**
 * KRA Photo Journal – Auto Tag Panel
 *
 * Admin panel to bulk auto-tag photos from folder names (shared rules in
 * @kra/photo-journal-shared `suggestFolderTags`), AI for kimono types the
 * rules can't read, and optional AI vision on cover photos.
 * Steps: dry-run preview → confirm → apply.
 */

import React, { useState, useCallback } from "react";
import type { AutoTagResult } from "../lib/api.js";
import { runAutoTag } from "../lib/api.js";

interface AutoTagPanelProps {
  onClose: () => void;
}

type Phase = "config" | "scanning" | "preview" | "applying" | "done" | "error";

export function AutoTagPanel({ onClose }: AutoTagPanelProps) {
  const [phase, setPhase] = useState<Phase>("config");
  const [useAI, setUseAI] = useState(true);
  const [useVision, setUseVision] = useState(false);
  const [results, setResults] = useState<AutoTagResult[]>([]);
  const [stats, setStats] = useState({ updated: 0, skipped: 0 });
  const [errorMsg, setErrorMsg] = useState("");

  const handleDryRun = useCallback(async () => {
    setPhase("scanning");
    try {
      const resp = await runAutoTag({ dryRun: true, useAI, useVision });
      setResults(resp.results);
      setStats({ updated: resp.results.length, skipped: resp.skipped });
      setPhase("preview");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Unknown error");
      setPhase("error");
    }
  }, [useAI, useVision]);

  const handleApply = useCallback(async () => {
    setPhase("applying");
    try {
      const resp = await runAutoTag({ dryRun: false, useAI, useVision });
      setStats({ updated: resp.updated, skipped: resp.skipped });
      setResults(resp.results);
      setPhase("done");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Unknown error");
      setPhase("error");
    }
  }, [useAI, useVision]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm pt-[10vh] px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-1">
          <div>
            <h2 className="text-base font-semibold text-ink-0">Auto Tag</h2>
            <p className="text-2xs text-ink-3 mt-0.5">
              Tự động gắn tag từ tên folder + AI Vision
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-3 hover:text-ink-0 transition-colors p-1"
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {phase === "config" && (
            <div className="space-y-4">
              <div className="bg-surface-0 rounded-xl p-4 space-y-3">
                <p className="text-sm text-ink-1">
                  Scan tất cả bộ ảnh có <span className="font-medium">Admin folder name</span> và tự gắn tag dựa trên tên folder:
                </p>
                <ul className="text-2xs text-ink-2 space-y-1 ml-4 list-disc">
                  <li><span className="font-medium">Loại kimono:</span> "furisode", "cao cấp" → premium furisode, "grand premium", "ren"/"lace", "truyền thống", "yukata", "basic", "hakama", "big size" (ưu tiên folder danh mục)</li>
                  <li><span className="font-medium">Màu sắc:</span> "đen", "đỏ", "hồng", "trắng", "xanh", "vàng", "tím", "cam", "đen vàng", "trắng vàng" (cả tiếng Anh)</li>
                  <li><span className="font-medium">Kiểu chụp:</span> "couple"/"kimono đôi", "family", "trùm khăn"/"indo" → Indonesia, "studio"</li>
                  <li><span className="font-medium">Mùa:</span> "sakura", "momiji", "summer", "cẩm tú cầu", "pháo hoa", "hello kitty"</li>
                  <li><span className="font-medium">Cảnh:</span> "night"/"đêm", "sensoji", "nezu", "skytree", "sumida", "ueno", "hirai"</li>
                </ul>
                <p className="text-2xs text-ink-3 bg-green-50 rounded-lg px-3 py-2">
                  Chỉ gắn tag khi field đang trống. Không ghi đè tag đã có.
                </p>
              </div>

              <label className="flex items-center gap-3 bg-surface-0 rounded-xl p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  className="w-4 h-4 rounded accent-[#B76E6E]"
                />
                <div>
                  <p className="text-sm text-ink-1 font-medium">AI đọc tên folder</p>
                  <p className="text-2xs text-ink-3">
                    Tên viết tắt / sai chính tả mà luật không đọc ra loại kimono thì AI đoán. Tag do AI có dấu ✨ trong preview.
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 bg-surface-0 rounded-xl p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useVision}
                  onChange={(e) => setUseVision(e.target.checked)}
                  className="w-4 h-4 rounded accent-[#B76E6E]"
                />
                <div>
                  <p className="text-sm text-ink-1 font-medium">AI nhìn ảnh cover</p>
                  <p className="text-2xs text-ink-3">
                    Khi tên không có màu, AI nhìn ảnh cover để đoán màu kimono + ảnh đêm. Chạy lúc gắn tag (không hiện trong preview), chậm với nhiều bộ.
                  </p>
                </div>
              </label>
            </div>
          )}

          {phase === "scanning" && (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="w-8 h-8 border-2 border-[#B76E6E] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-ink-2">Đang scan tên folder{useAI ? " (có AI)" : ""}...</p>
            </div>
          )}

          {phase === "preview" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#B76E6E]/10 text-[#B76E6E] rounded-full px-3 py-1 text-sm font-medium">
                  {stats.updated} bộ ảnh sẽ được tag
                </div>
                <span className="text-2xs text-ink-3">{stats.skipped} bỏ qua (đã có tag hoặc không match)</span>
              </div>

              {results.length === 0 ? (
                <p className="text-sm text-ink-2 text-center py-6">
                  Không tìm thấy bộ ảnh nào cần tag.
                </p>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {results.map((r) => (
                    <div key={r.recordId} className="bg-surface-0 rounded-xl px-4 py-3">
                      <p className="text-sm text-ink-1 font-medium truncate">{r.folderName}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {Object.entries(r.tagsAdded).map(([group, tags]) =>
                          (tags as string[]).map((tag) => (
                            <span
                              key={`${group}-${tag}`}
                              className="text-2xs px-2 py-0.5 rounded-full bg-[#B76E6E]/10 text-[#B76E6E]"
                            >
                              {groupEmoji(group)} {tag}{r.aiTags?.includes(tag) ? " ✨" : ""}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {phase === "applying" && (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="w-8 h-8 border-2 border-[#B76E6E] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-ink-2">Đang ghi tag vào Airtable...</p>
            </div>
          )}

          {phase === "done" && (
            <div className="text-center py-8 space-y-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="text-sm text-ink-1 font-medium">Đã gắn tag cho {stats.updated} bộ ảnh</p>
              <p className="text-2xs text-ink-3">{stats.skipped} bộ ảnh bỏ qua</p>
            </div>
          )}

          {phase === "error" && (
            <div className="text-center py-8 space-y-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>
              <p className="text-sm text-red-600 font-medium">Lỗi</p>
              <p className="text-2xs text-ink-3">{errorMsg}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-surface-1 flex justify-end gap-3">
          {phase === "config" && (
            <>
              <button onClick={onClose} className="text-sm text-ink-2 px-4 py-2 rounded-xl hover:bg-surface-1 transition-colors">
                Hủy
              </button>
              <button
                onClick={handleDryRun}
                className="text-sm text-white bg-[#B76E6E] px-5 py-2 rounded-xl hover:bg-[#a55e5e] transition-colors font-medium"
              >
                Scan & Preview
              </button>
            </>
          )}

          {phase === "preview" && results.length > 0 && (
            <>
              <button onClick={() => setPhase("config")} className="text-sm text-ink-2 px-4 py-2 rounded-xl hover:bg-surface-1 transition-colors">
                Quay lại
              </button>
              <button
                onClick={handleApply}
                className="text-sm text-white bg-[#B76E6E] px-5 py-2 rounded-xl hover:bg-[#a55e5e] transition-colors font-medium"
              >
                Gắn tag cho {stats.updated} bộ ảnh
              </button>
            </>
          )}

          {(phase === "preview" && results.length === 0) && (
            <button onClick={onClose} className="text-sm text-ink-2 px-4 py-2 rounded-xl hover:bg-surface-1 transition-colors">
              Đóng
            </button>
          )}

          {(phase === "done" || phase === "error") && (
            <button onClick={onClose} className="text-sm text-ink-2 px-4 py-2 rounded-xl hover:bg-surface-1 transition-colors">
              Đóng
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function groupEmoji(group: string): string {
  switch (group) {
    case "kimono_tag": return "👘";
    case "kimono_color_tag": return "🎨";
    case "shoot_tag": return "📷";
    case "season_tag": return "🌸";
    case "scene_tag": return "🏯";
    default: return "";
  }
}
